// Line-count audit (2026-05-04): 247 lines.
import { useCallback } from 'react';
import { isContainerType } from '@/utils/componentTypes';
import { applyPageMentionToButton } from '@/services/ai/mentions';
import { ComponentType } from '@/types';
import type { Component, ComponentInput } from '@/types';
import type { AnyComponentProperties } from '@/types/component-properties';
import type { ComponentData, LayoutData, PropertyMessage } from '@/components/ai/types';
import {
  getDefaultProperties,
  sanitizeUntrustedCreationProperties,
} from '@/services/ai/commands/utils/component-defaults';
import type { ComponentDefaultSourceContext } from '@/services/ai/commands/utils/component-defaults';
import type {
  CanvasPlacementInput,
  CanvasPlacementMessageOptions,
  UseCanvasPlacementDeps,
  UseCanvasPlacementReturn,
} from './agentMessagingTypes';
import { getBlockedAICommandPermissionMessage } from '@/services/ai/agentCommandPermissions';
import {
  isPlainRecord,
  mergeSanitizedRecords,
  sanitizeRecordValue,
} from '@/utils/safeRecordSanitization';

function isKnownComponentType(value: string): value is ComponentType {
  return Object.values(ComponentType).some((componentType) => componentType === value);
}

function appendAssistantMessage(
  previous: PropertyMessage[],
  id: string,
  content: string
): PropertyMessage[] {
  return [
    ...previous,
    {
      id,
      role: 'assistant',
      content,
      timestamp: new Date(),
    },
  ];
}

const MISSING_PAGE_ERROR = 'No active page. Please create or select a page before adding components.';

function isComponentDataForPlacement(value: unknown): value is ComponentData {
  return isPlainRecord(value) && !('pageId' in value) && typeof value.type === 'string';
}

function isComponentInput(value: unknown): value is ComponentInput {
  return (
    isPlainRecord(value) &&
    typeof value.pageId === 'string' &&
    typeof value.name === 'string' &&
    typeof value.type === 'string' &&
    isKnownComponentType(value.type) &&
    isPlainRecord(value.properties)
  );
}

function sanitizeComponentInput(componentInput: ComponentInput): ComponentInput | null {
  const sanitized = sanitizeRecordValue(componentInput);
  return isComponentInput(sanitized) ? sanitized : null;
}

function buildCreationProperties(
  type: ComponentType,
  title: string | undefined,
  aiProperties: unknown,
  aiData: unknown,
  flexChartProperties: Partial<AnyComponentProperties>,
  sourceContext: ComponentDefaultSourceContext | undefined
): AnyComponentProperties {
  const shouldStripDurableRecords = sourceContext?.mode === 'binding';
  const sanitizedAIProperties = sanitizeUntrustedCreationProperties(aiProperties, {
    stripDurableRecords: shouldStripDurableRecords,
  });
  const aiDataProperties = shouldStripDurableRecords || aiData === undefined
    ? undefined
    : { data: aiData };
  const titleProperties = title === undefined ? undefined : { title };

  return mergeSanitizedRecords(
    getDefaultProperties(type, title, { sourceContext }),
    sanitizedAIProperties,
    titleProperties,
    aiDataProperties,
    flexChartProperties
  );
}

function sanitizePlacementMetadata(metadata: unknown): Record<string, unknown> | undefined {
  const sanitizedMetadata = sanitizeUntrustedCreationProperties(metadata);
  return Object.keys(sanitizedMetadata).length > 0 ? sanitizedMetadata : undefined;
}

export function useCanvasPlacement(deps: UseCanvasPlacementDeps): UseCanvasPlacementReturn {
  const {
    project,
    component,
    components,
    multipleComponents,
    currentMentionContextRef,
    addComponent,
    moveComponent,
    selectMultipleComponents,
    setMessages,
    setError,
    generateMessageId,
    commandPermissionGuard,
  } = deps;

  const preflightInsertion = useCallback((
    options: CanvasPlacementMessageOptions = {}
  ): { projectId: string; targetPageId: string } | null => {
    if (!project?.id) {
      setError('No active project. Please create or open a project first.');
      return null;
    }

    const selectedComponent = component || multipleComponents?.[0];
    const insertionPermission = commandPermissionGuard?.({
      categories: ['insertion'],
      project,
      targetComponents: selectedComponent ? [selectedComponent] : [],
    });

    if (insertionPermission && !insertionPermission.allowed) {
      if (options.suppressAssistantMessage) {
        return null;
      }

      setMessages((previous) =>
        appendAssistantMessage(
          previous,
          generateMessageId(),
          getBlockedAICommandPermissionMessage(insertionPermission, 'insertion')
        )
      );
      return null;
    }

    const targetPageId = project.currentPageId;
    if (!targetPageId) {
      setError(MISSING_PAGE_ERROR);
      return null;
    }

    return { projectId: project.id, targetPageId };
  }, [
    project,
    component,
    multipleComponents,
    setMessages,
    setError,
    generateMessageId,
    commandPermissionGuard,
  ]);

  const buildComponentInput = useCallback((
    componentData: ComponentData,
    projectId: string,
    targetPageId: string,
    sourceContext: ComponentDefaultSourceContext | undefined
  ): ComponentInput => {
    const selectedComponent = component || multipleComponents?.[0];
    const isContainer = selectedComponent ? isContainerType(selectedComponent.type) : false;

    let parentId: string | undefined;
    let position = {
      x: componentData.placement?.position?.x ?? 50,
      y: componentData.placement?.position?.y ?? 50,
      z: 1,
    };
    let size = {
      width: componentData.placement?.size?.width ?? 400,
      height: componentData.placement?.size?.height ?? 300,
    };
    let responsiveStrategy: 'absolute' | 'relative' = 'absolute';

    if (selectedComponent && isContainer) {
      parentId = selectedComponent.id;
      position = { x: 0, y: 0, z: 1 };
      responsiveStrategy = 'relative';

      if (componentData.type.includes('chart')) {
        size = { width: 400, height: 400 };
      } else if (componentData.type === 'button') {
        size = { width: 120, height: 40 };
      } else if (componentData.type === 'text') {
        size = { width: 200, height: 60 };
      } else {
        size = { width: 200, height: 100 };
      }
    }

    const resolvedComponentType = isKnownComponentType(componentData.type)
      ? componentData.type
      : ComponentType.CUSTOM;

    const flexChartProperties: Partial<AnyComponentProperties> =
      parentId && selectedComponent?.type === ComponentType.FLEX_CONTAINER && componentData.type.includes('chart')
        ? { flex: '1 1 auto', minHeight: 400 }
        : {};
    const properties = buildCreationProperties(
      resolvedComponentType,
      componentData.title,
      componentData.properties,
      componentData.data,
      flexChartProperties,
      sourceContext
    );

    const terraMatrixComponent: ComponentInput = {
      projectId,
      pageId: targetPageId,
      name: componentData.title || `New ${componentData.type}`,
      type: resolvedComponentType,
      properties,
      position,
      size,
      style: {
        backgroundColor: 'transparent',
        borderRadius: '8px',
        padding: '16px',
        border: '1px solid #e5e7eb',
      },
      responsivePosition: {
        strategy: responsiveStrategy,
        constraints: parentId && selectedComponent?.type === ComponentType.FLEX_CONTAINER && componentData.type.includes('chart')
          ? {
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: '0',
            }
          : undefined,
        responsiveSize: parentId && selectedComponent?.type === ComponentType.FLEX_CONTAINER && componentData.type.includes('chart')
          ? {
              width: '100%',
              height: '100%',
              minHeight: 400,
            }
          : undefined,
        breakpoints: undefined,
        layoutMode: undefined,
      },
      parentId,
      children: [],
      order: Date.now(),
      locked: false,
      hidden: false,
      version: 1,
      createdBy: 'ai-assistant',
      lastModifiedBy: 'ai-assistant',
      lastModifiedAt: new Date(),
      isDeleted: false,
    };

    const sanitizedComponent = sanitizeComponentInput(terraMatrixComponent);
    if (!sanitizedComponent) {
      throw new Error('Failed to prepare a safe component placement payload.');
    }

    const { resolvedMessage, pageContext: storedPageContext } = currentMentionContextRef.current;
    if (
      sanitizedComponent.type === ComponentType.BUTTON
      && storedPageContext
      && resolvedMessage?.resolvedMentions
    ) {
      const updatedComponent = applyPageMentionToButton(
        sanitizedComponent,
        resolvedMessage.resolvedMentions
      );

      if (updatedComponent !== sanitizedComponent) {
        const sanitizedUpdatedComponent = sanitizeComponentInput({
          ...sanitizedComponent,
          properties: mergeSanitizedRecords(
            sanitizedComponent.properties,
            updatedComponent.properties
          ),
        });
        if (!sanitizedUpdatedComponent) {
          throw new Error('Failed to prepare a safe component placement payload.');
        }

        return sanitizedUpdatedComponent;
      }
    }

    return sanitizedComponent;
  }, [
    component,
    multipleComponents,
    currentMentionContextRef,
  ]);

  const handlePlaceComponent = useCallback(
    async (
      componentData: ComponentData,
      options: CanvasPlacementMessageOptions = {}
    ): Promise<boolean> => {
      try {
        const placementTarget = preflightInsertion(options);
        if (!placementTarget) {
          return false;
        }

        const finalComponent = buildComponentInput(
          componentData,
          placementTarget.projectId,
          placementTarget.targetPageId,
          options.sourceContext
        );
        addComponent(finalComponent);
        currentMentionContextRef.current = { resolvedMessage: null, pageContext: null };

        if (!options.suppressAssistantMessage) {
          setMessages((previous) =>
            appendAssistantMessage(
              previous,
              generateMessageId(),
              `✅ ${componentData.title} has been added to your canvas!`
            )
          );
        }
        return true;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        setError(`Failed to place component: ${message}`);
        return false;
      }
    },
    [
      preflightInsertion,
      buildComponentInput,
      currentMentionContextRef,
      addComponent,
      setMessages,
      setError,
      generateMessageId,
    ]
  );

  const handlePlaceComponents = useCallback(
    async (
      plannedComponents: CanvasPlacementInput[],
      successMessage?: string,
      options: CanvasPlacementMessageOptions = {}
    ): Promise<boolean> => {
      try {
        if (plannedComponents.length === 0) {
          return false;
        }

        const placementTarget = preflightInsertion(options);
        if (!placementTarget) {
          return false;
        }

        const preparedComponents: ComponentInput[] = plannedComponents.map((plannedComponent) => {
          if (!isPlainRecord(plannedComponent)) {
            throw new Error('Rejected malformed component placement payload.');
          }

          if (isComponentDataForPlacement(plannedComponent)) {
            return buildComponentInput(
              plannedComponent,
              placementTarget.projectId,
              placementTarget.targetPageId,
              options.sourceContext
            );
          }

          if (!isComponentInput(plannedComponent)) {
            throw new Error('Rejected malformed component placement payload.');
          }

          const sanitizedPlannedComponent = sanitizeComponentInput({
            ...plannedComponent,
            projectId: placementTarget.projectId,
            pageId: placementTarget.targetPageId,
            metadata: sanitizePlacementMetadata(plannedComponent.metadata),
            properties: buildCreationProperties(
              plannedComponent.type,
              plannedComponent.name,
              plannedComponent.properties,
              undefined,
              {},
              options.sourceContext
            ),
          });
          if (!sanitizedPlannedComponent) {
            throw new Error('Rejected malformed component placement payload.');
          }

          return sanitizedPlannedComponent;
        });

        preparedComponents.forEach((plannedComponent) => {
          addComponent(plannedComponent);
        });
        currentMentionContextRef.current = { resolvedMessage: null, pageContext: null };

        if (!options.suppressAssistantMessage) {
          setMessages((previous) =>
            appendAssistantMessage(
              previous,
              generateMessageId(),
              successMessage || `✅ Added ${plannedComponents.length} components to your canvas!`
            )
          );
        }
        return true;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        setError(`Failed to place components: ${message}`);
        return false;
      }
    },
    [
      preflightInsertion,
      buildComponentInput,
      currentMentionContextRef,
      addComponent,
      setMessages,
      setError,
      generateMessageId,
    ]
  );

  const handleApplyLayout = useCallback(
    async (layoutData: LayoutData): Promise<void> => {
      try {
        if (!project?.id) {
          setError('No active project. Please create or open a project first.');
          return;
        }

        const targetComponentIds = [
          ...new Set(layoutData.instructions.flatMap((instruction) => [
            instruction.componentId,
            ...(instruction.componentIds || []),
          ]).filter((id): id is string => typeof id === 'string' && id.length > 0)),
        ];
        const componentLookup = new Map(
          [
            ...components,
            ...(component ? [component] : []),
            ...(multipleComponents || []),
          ].map((target) => [target.id, target])
        );
        const targetComponents = targetComponentIds
          .map((id) => componentLookup.get(id))
          .filter((target): target is Component => target !== undefined);
        const layoutPermission = commandPermissionGuard?.({
          categories: ['layout'],
          project,
          targetComponentIds,
          targetComponents,
        });

        if (layoutPermission && !layoutPermission.allowed) {
          setMessages((previous) =>
            appendAssistantMessage(
              previous,
              generateMessageId(),
              getBlockedAICommandPermissionMessage(layoutPermission, 'layout')
            )
          );
          return;
        }

        for (const instruction of layoutData.instructions) {
          switch (instruction.action) {
            case 'move':
              if (instruction.componentId && instruction.position) {
                moveComponent(instruction.componentId, {
                  x: instruction.position.x,
                  y: instruction.position.y,
                  z: 1,
                });
              }
              break;
            case 'align':
              if (instruction.componentIds && instruction.componentIds.length > 1) {
                selectMultipleComponents(instruction.componentIds);
              }
              break;
            default:
          }
        }

        setMessages((previous) =>
          appendAssistantMessage(
            previous,
            generateMessageId(),
            `✅ Layout arrangement applied! ${layoutData.description}`
          )
        );
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        setError(`Failed to apply layout: ${message}`);
      }
    },
    [
      project,
      component,
      components,
      multipleComponents,
      moveComponent,
      selectMultipleComponents,
      setMessages,
      setError,
      generateMessageId,
      commandPermissionGuard,
    ]
  );

  return {
    handlePlaceComponent,
    handlePlaceComponents,
    handleApplyLayout,
  };
}
